import type { AdminAiScanFindingDetailsProps } from "../../models/admin-ai-scans.models";
import { ADMIN_AI_SCAN_FINDING_TECHNICAL_FIELDS } from "../../utils/admin-ai-scans.constants";
import { getAdminAiScanFindingFieldLabel, getAdminAiScanText } from "../../utils/admin-ai-scans.utils";
import AdminAiScanJsonDetails from "./AdminAiScanJsonDetails";

// Give the full explanation and remediation a compact label/value layout, retaining every original field below.
const AdminAiScanFindingDetails = ({ finding, findingFields, source }: AdminAiScanFindingDetailsProps) => {
  const readableFields = findingFields.filter(
    (field) => finding[field] !== undefined && !ADMIN_AI_SCAN_FINDING_TECHNICAL_FIELDS.has(field),
  );
  const technicalFields = findingFields.filter(
    (field) => finding[field] !== undefined && ADMIN_AI_SCAN_FINDING_TECHNICAL_FIELDS.has(field),
  );

  return (
    <div className="min-w-0" data-test="admin-ai-scan-finding-full-details">
      <dl className="flex flex-wrap gap-x-6 gap-y-2 border-b border-slate-200 px-4 py-3 text-xs text-slate-600 dark:border-gray-600 dark:text-gray-300">
        {["status", "severity", "category", "layer"].map(
          (field) =>
            finding[field] !== undefined && (
              <div key={field} className="flex flex-wrap gap-1">
                <dt className="font-semibold">{getAdminAiScanFindingFieldLabel(field)}:</dt>
                <dd className="break-words">{getAdminAiScanText(finding[field])}</dd>
              </div>
            ),
        )}
      </dl>
      <dl className="divide-y divide-slate-200 dark:divide-gray-600" data-test="admin-ai-scan-finding-explanation">
        {readableFields.map((field) => {
          const value = finding[field];
          const isNested = value !== null && typeof value === "object";

          return (
            <div key={field} className="grid min-w-0 gap-2 px-4 py-3 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-5">
              <dt className="text-xs font-semibold leading-6 text-slate-600 dark:text-gray-300">
                {getAdminAiScanFindingFieldLabel(field)}
              </dt>
              <dd className="min-w-0 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700 dark:text-gray-200">
                {isNested ? <AdminAiScanJsonDetails label="Details" value={value} /> : String(value)}
              </dd>
            </div>
          );
        })}
      </dl>
      <details
        className="border-t border-slate-200 px-4 py-3 dark:border-gray-600"
        data-test="admin-ai-scan-finding-technical-details"
      >
        <summary className="cursor-pointer text-sm font-semibold text-slate-600 focus-visible:outline-green dark:text-gray-300">
          Technical details and original source
        </summary>
        <div className="mt-3 space-y-3">
          <AdminAiScanJsonDetails label="Finding ID" value={finding.id ?? "Unspecified"} />
          {technicalFields.map((field) => (
            <AdminAiScanJsonDetails key={field} label={getAdminAiScanFindingFieldLabel(field)} value={finding[field]} />
          ))}
          <AdminAiScanJsonDetails label="Complete finding" value={finding} />
          {source && (
            <>
              <AdminAiScanJsonDetails label="Report file" value={source.file.path} />
              <AdminAiScanJsonDetails label="Complete source report" value={source.file.report} />
            </>
          )}
        </div>
      </details>
    </div>
  );
};

export default AdminAiScanFindingDetails;
